// Chakra imports
import {
    Badge,
    Divider,
    Flex,
    HStack,
    Icon,
    IconButton,
    Link,
    SimpleGrid,
    Text,
    Tooltip,
    useClipboard,
    useColorModeValue,
} from "@chakra-ui/react";
// Custom components
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import React from "react";
import {FaGlobe, FaWhatsapp} from "react-icons/fa";
import {CheckIcon, CopyIcon, EmailIcon, ExternalLinkIcon, PhoneIcon} from "@chakra-ui/icons";
import {FiMapPin, FiUsers, FiHome, FiMonitor} from "react-icons/fi";

// A single label/value row. Renders "—" when the value is missing so the layout
// stays consistent regardless of how complete the school record is.
const InfoRow = ({label, value, children}) => {
    const textColor = useColorModeValue("gray.700", "white");
    const mutedColor = useColorModeValue("gray.500", "gray.400");
    return (
        <Flex align="center" justify="space-between" gap="12px">
            <Text fontSize="sm" color={mutedColor} fontWeight="500">
                {label}
            </Text>
            {children || (
                <Text fontSize="sm" color={textColor} fontWeight="600" noOfLines={1} textAlign="right">
                    {value || "—"}
                </Text>
            )}
        </Flex>
    );
};

const ProfileInformation = ({title, school = {}}) => {
    // Chakra color mode
    const textColor = useColorModeValue("gray.700", "white");
    const mutedColor = useColorModeValue("gray.500", "gray.400");
    const linkColor = useColorModeValue("teal.500", "teal.300");

    const {
        description,
        name,
        phone,
        email,
        country,
        address,
        website,
        type,
        subType,
        totalStudent,
        totalClass,
        totalRoom,
    } = school;

    // The detail API exposes the class count as `totalRoom`; fall back to the
    // legacy `totalClass` if it's ever present.
    const totalClasses = totalRoom ?? totalClass;

    // AnyDesk remote-support address. Accept a few likely field spellings so the
    // row works regardless of exactly what the backend returns.
    const anyDeskId = school?.anyDeskId ?? school?.anyDeskNumber ?? school?.anydesk ?? "";
    // The AnyDesk client launches via the `anydesk:<id>` URI scheme; strip any
    // spaces so a copy-pasted "123 456 789" still produces a valid deep link.
    const anyDeskConnect = String(anyDeskId).replace(/\s+/g, "");
    const anyDeskLink = anyDeskConnect ? `anydesk:${anyDeskConnect}` : "";
    const {hasCopied: anyDeskCopied, onCopy: onCopyAnyDesk} = useClipboard(String(anyDeskId));

    // WhatsApp deep link — strip everything except digits so numbers formatted
    // with spaces, dashes or a leading "+" still produce a valid wa.me URL.
    const whatsappNumber = String(phone || "").replace(/\D/g, "");
    const whatsappLink = whatsappNumber ? `https://wa.me/${whatsappNumber}` : "";

    // Normalise the website into an absolute URL so the link always works.
    const websiteHref = website
        ? (/^https?:\/\//i.test(website) ? website : `https://${website}`)
        : "";

    return (
        <Card p="16px" my={{sm: "24px", xl: "0px"}}>
            <CardHeader p="12px 5px" mb="12px">
                <Flex justify="space-between" align="center" w="100%">
                    <Text fontSize="lg" color={textColor} fontWeight="bold">
                        {title}
                    </Text>
                    {(type || subType) && (
                        <HStack spacing="6px">
                            {type && (
                                <Badge colorScheme="purple" variant="subtle" borderRadius="full" px="8px"
                                       textTransform="none">
                                    {type}
                                </Badge>
                            )}
                            {subType && (
                                <Badge colorScheme="blue" variant="subtle" borderRadius="full" px="8px"
                                       textTransform="none">
                                    {subType}
                                </Badge>
                            )}
                        </HStack>
                    )}
                </Flex>
            </CardHeader>
            <CardBody px="5px">
                <Flex direction="column" w="100%">
                    {description ? (
                        <Text fontSize="sm" color={mutedColor} fontWeight="400" mb="18px">
                            {description}
                        </Text>
                    ) : null}

                    <Flex direction="column" gap="14px">
                        <InfoRow label="Full Name" value={name}/>

                        <InfoRow label="Mobile">
                            <HStack spacing="8px">
                                <Icon as={PhoneIcon} color={mutedColor} boxSize="12px"/>
                                <Text fontSize="sm" color={textColor} fontWeight="600">
                                    {phone || "—"}
                                </Text>
                                {whatsappLink && (
                                    <Link
                                        href={whatsappLink}
                                        isExternal
                                        color="whatsapp.500"
                                        fontSize="lg"
                                        aria-label="Chat on WhatsApp"
                                        title="Chat on WhatsApp"
                                        _hover={{color: "whatsapp.600"}}>
                                        <Icon as={FaWhatsapp}/>
                                    </Link>
                                )}
                            </HStack>
                        </InfoRow>

                        <InfoRow label="Email">
                            {email ? (
                                <Link href={`mailto:${email}`} color={linkColor} fontSize="sm" fontWeight="600"
                                      noOfLines={1}>
                                    <HStack spacing="8px">
                                        <Icon as={EmailIcon} boxSize="12px"/>
                                        <Text as="span">{email}</Text>
                                    </HStack>
                                </Link>
                            ) : (
                                <Text fontSize="sm" color={textColor} fontWeight="600">—</Text>
                            )}
                        </InfoRow>

                        <InfoRow label="Address">
                            <HStack spacing="8px" maxW="60%">
                                <Icon as={FiMapPin} color={mutedColor} boxSize="12px"/>
                                <Text fontSize="sm" color={textColor} fontWeight="600" noOfLines={1} textAlign="right">
                                    {address || country || "—"}
                                </Text>
                            </HStack>
                        </InfoRow>

                        <InfoRow label="Website">
                            {websiteHref ? (
                                <Link href={websiteHref} isExternal color={linkColor} fontSize="sm" fontWeight="600"
                                      noOfLines={1}>
                                    <HStack spacing="8px">
                                        <Icon as={FaGlobe} boxSize="12px"/>
                                        <Text as="span">{website}</Text>
                                    </HStack>
                                </Link>
                            ) : (
                                <Text fontSize="sm" color={textColor} fontWeight="600">—</Text>
                            )}
                        </InfoRow>

                        {/* AnyDesk — interactive: copy the id to the clipboard, or
                            launch the AnyDesk client straight into a session. */}
                        <InfoRow label="AnyDesk">
                            {anyDeskId ? (
                                <HStack spacing="6px">
                                    <Icon as={FiMonitor} color={mutedColor} boxSize="12px"/>
                                    <Text fontSize="sm" color={textColor} fontWeight="600" noOfLines={1}>
                                        {anyDeskId}
                                    </Text>
                                    <Tooltip label={anyDeskCopied ? "Copied!" : "Copy AnyDesk ID"}
                                             placement="top" hasArrow>
                                        <IconButton
                                            aria-label="Copy AnyDesk ID"
                                            icon={anyDeskCopied ? <CheckIcon/> : <CopyIcon/>}
                                            size="xs"
                                            variant="ghost"
                                            colorScheme={anyDeskCopied ? "green" : "gray"}
                                            minW="auto"
                                            h="18px"
                                            onClick={onCopyAnyDesk}
                                        />
                                    </Tooltip>
                                    <Tooltip label="Connect via AnyDesk" placement="top" hasArrow>
                                        <Link href={anyDeskLink} aria-label="Connect via AnyDesk"
                                              color={linkColor} _hover={{color: "teal.600"}}>
                                            <Icon as={ExternalLinkIcon} boxSize="14px"/>
                                        </Link>
                                    </Tooltip>
                                </HStack>
                            ) : (
                                <Text fontSize="sm" color={textColor} fontWeight="600">—</Text>
                            )}
                        </InfoRow>
                    </Flex>

                    <Divider my="18px"/>

                    {/* Quick stats — students & classes at a glance. */}
                    <SimpleGrid columns={2} spacing="12px">
                        <Flex align="center" gap="10px">
                            <Flex align="center" justify="center" boxSize="34px" borderRadius="10px" bg="teal.400">
                                <Icon as={FiUsers} color="white" boxSize="16px"/>
                            </Flex>
                            <Flex direction="column">
                                <Text fontSize="lg" fontWeight="bold" color={textColor} lineHeight="1">
                                    {totalStudent ?? "—"}
                                </Text>
                                <Text fontSize="xs" color={mutedColor}>Students</Text>
                            </Flex>
                        </Flex>
                        <Flex align="center" gap="10px">
                            <Flex align="center" justify="center" boxSize="34px" borderRadius="10px" bg="blue.400">
                                <Icon as={FiHome} color="white" boxSize="16px"/>
                            </Flex>
                            <Flex direction="column">
                                <Text fontSize="lg" fontWeight="bold" color={textColor} lineHeight="1">
                                    {totalClasses ?? "—"}
                                </Text>
                                <Text fontSize="xs" color={mutedColor}>Classes</Text>
                            </Flex>
                        </Flex>
                    </SimpleGrid>
                </Flex>
            </CardBody>
        </Card>
    );
};

export default ProfileInformation;
